import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { SingularStationArea } from '@thive/fddm-types';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo } from 'react';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { useHasOmniVehicles } from '@/modules/vehicles/hooks/useHasOmniVehicles';
import { CrabSettingInput } from '../common/CrabSettingInput';

type Props = {
  selectedSingularStationArea: SingularStationArea;
};

function SingularStationPoiPreferencesComponent({ selectedSingularStationArea }: Props) {
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const hasOmniVehicles = useHasOmniVehicles();
  const crabSetting = selectedSingularStationArea.loadHandlingPoiSpecs[0]?.crabSetting ?? 0;

  const handleCrabChange = useCallback(
    (value: number) => {
      fddmSvc.setPreference('singularStationArea.loadHandlingPoiSpecs.crabSetting', value);
      fddmSvc.updateArea({
        ...selectedSingularStationArea,
        loadHandlingPoiSpecs: [
          {
            ...selectedSingularStationArea.loadHandlingPoiSpecs[0],
            crabSetting: value,
          },
        ],
      });
      const smoothHeadingValue =
        value !== 0 ? true : fddmSvc.getPreference('connection.smoothHeading');
      const relatedConnections = useFloorplanDesignStore
        .getState()
        .interAreaConnectionActions.getRelatedTo([selectedSingularStationArea.id]);
      if (relatedConnections.length > 0) {
        fddmSvc.updateInterAreaConnections(
          relatedConnections.map((conn) => ({
            ...conn,
            aToBConnections: conn.aToBConnections.map((c) => ({
              ...c,
              smoothHeading: smoothHeadingValue,
            })),
            bToAConnections: conn.bToAConnections.map((c) => ({
              ...c,
              smoothHeading: smoothHeadingValue,
            })),
          })),
        );
      }
    },
    [fddmSvc, selectedSingularStationArea],
  );

  if (!hasOmniVehicles) return null;

  return (
    <Stack spacing={1}>
      <PropertiesLabel i18nkey='interface:properties.area.crab_setting'>
        <CrabSettingInput value={crabSetting} onChange={handleCrabChange} />
      </PropertiesLabel>
    </Stack>
  );
}

export const SingularStationPoiPreferences = memo(SingularStationPoiPreferencesComponent);
